import { useAuth } from '@/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    createFileRoute,
    redirect,
    useRouter,
    useRouterState,
} from '@tanstack/react-router'
import { useState, useRef } from 'react'
import { z } from 'zod'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'


const fallback = '/home' as const

export const Route = createFileRoute('/sign-in')({
    validateSearch: z.object({
        redirect: z.string().optional().catch(''),
    }),
    beforeLoad: ({ context, search }) => {
        if (context.auth.isAuthenticated) {
            throw redirect({ to: search.redirect || fallback })
        }
    },
    component: LoginComponent,
})

function LoginComponent() {
    const auth = useAuth()
    const router = useRouter()
    const isLoading = useRouterState({ select: (s) => s.isLoading })
    const navigate = Route.useNavigate()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const passwordRef = useRef<HTMLInputElement>(null)
    const search = Route.useSearch()

    const onFormSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
        setIsSubmitting(true)
        setError(null)
        try {
            evt.preventDefault()
            const data = new FormData(evt.currentTarget)
            const fieldValue = data.get('username')
            const passwordField = data.get('password')

            if (!fieldValue) {
                setError('Username is required')
                return
            }
            if (!passwordField) {
                setError('Password is required')
                return
            }
            const username = fieldValue.toString()
            const password = passwordField?.toString()
            await auth.login(username, password)

            await router.invalidate()
            await navigate({ to: search.redirect || fallback })
        } catch (error) {
            console.error('Error logging in: ', error)
            setError('Invalid username or password')
            // Clear the password field
            if (passwordRef.current) {
                passwordRef.current.value = ''
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    const isLoggingIn = isLoading || isSubmitting

    return (
        <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
            <div className="flex w-full max-w-sm flex-col gap-6">
                <a href="#" className="flex items-center gap-2 self-center font-medium">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md  text-primary-foreground">
                        <img src="favicon-32x32.png" className="size-8" />
                    </div>
                    CoolFocus
                </a>
                <div className={"flex flex-col gap-6"}>
                    <Card>
                        <CardHeader className="text-center">
                            <CardTitle className="text-xl">Welcome back</CardTitle>
                            <CardDescription>
                                Login with your CoolFocus Account
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={onFormSubmit}>
                                <div className="grid gap-6">
                                    {error && (
                                        <Alert variant="destructive" className="bg-red-50">
                                            <AlertCircle className="h-4 w-4" />
                                            <AlertDescription>{error}</AlertDescription>
                                        </Alert>
                                    )}
                                    <div className="grid gap-6">
                                        <div className="grid gap-2">
                                            <Label htmlFor="username">User Name</Label>
                                            <Input
                                                id="username"
                                                name="username"
                                                type="text"
                                                placeholder="Enter your username"
                                                required
                                                autoFocus
                                                autoComplete="username"
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <div className="flex items-center">
                                                <Label htmlFor="password">Password</Label>
                                                <a
                                                    tabIndex={99}
                                                    href="#"
                                                    className="ml-auto text-sm underline-offset-4 hover:underline"
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        navigate({ to: '/forgot-password' })
                                                    }}
                                                >
                                                    Forgot your password?
                                                </a>
                                            </div>
                                            <Input
                                                ref={passwordRef}
                                                id="password"
                                                name="password"
                                                type="password"
                                                placeholder="Enter your password"
                                                autoComplete="current-password"
                                                required
                                            />
                                        </div>
                                        <Button type="submit" className="w-full" disabled={isLoggingIn}>
                                            {isLoggingIn ? 'Logging in...' : 'Login'}
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                    <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary  ">
                        By clicking continue, you agree to our <a href="#">Terms of Service</a>{" "}
                        and <a href="#">Privacy Policy</a>.
                    </div>
                </div>
            </div>
        </div>
    )
}

